import React from "react";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveWbsConfiguration,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import { Context } from "../context";
import { Props } from "./types";
import styles from "./styles.scss";

const fixedColumns = [
  "statuses",
  "tags",
  "allocations",
  "endDate",
  "completionDate",
];

function SecondaryCriteria({ project, children }: Props) {
  const { configuration: providedConfiguration, style = {} } =
    React.useContext(Context);
  const colWidth = style.colWidth
    ? typeof style.colWidth === "number"
      ? `${style.colWidth}px`
      : style.colWidth
    : "9rem";
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { configuration } = useRetrieveWbsConfiguration(planzone);
  const roles = useRoles({ project });
  const foundProfile = providedConfiguration
    ? { name: "", roles, columns: providedConfiguration }
    : configuration.find((p) =>
        p.roles.find((role) =>
          roles.find((r) => r.uuid === role || r.name === role),
        ),
      ) ||
      (configuration.length > 0
        ? configuration[0]
        : {
            name: "",
            roles: [],
            columns: [],
          });
  const profile = providedConfiguration
    ? foundProfile
    : { ...foundProfile, columns: [...foundProfile.columns, { key: "meta" }] };
  const hasMeta = !!profile.columns.find(({ key }) => key === "meta");
  const columns = profile.columns.filter(
    ({ key }) =>
      !![...fixedColumns, ...template.map(({ uuid }) => uuid)].find(
        (criteria) => criteria === key,
      ),
  );
  const colStyle = {
    width: 0,
    minWidth: colWidth,
  };
  const { main, criteria, comments, files, contextualMenu } = children();

  return (
    <div className={styles.secondaryCriteria}>
      <div className={styles.row}>
        <div
          style={{
            minWidth: `calc(100% - ${colWidth} * ${columns.length + (providedConfiguration ? 0 : 1)})`,
          }}
        >
          {main}
        </div>
        {columns.map(({ key }) => (
          <div key={key} style={colStyle}>
            {criteria[key as keyof typeof criteria]}
          </div>
        ))}
        {hasMeta && (
          <div className={styles.flexContainer}>
            {comments ? (
              <div className={`${styles.comment} ${styles.comments}`}>
                {comments}
              </div>
            ) : (
              <div className={styles.spacer} />
            )}
            {files ? (
              <div className={`${styles.file} ${styles.file}`}>{files}</div>
            ) : (
              <div className={styles.spacer} />
            )}
          </div>
        )}
      </div>
      {contextualMenu}
    </div>
  );
}

export { SecondaryCriteria };
